/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import * as React from 'react';
import classnames from 'classnames';
import isNumber from 'lodash/isNumber';

import Overlay from 'components/overlay/Overlay';

import TileStandardOverlay from 'modules/freemium/components/tile-standard-overlay/TileStandardOverlay';
import TileStandardContext from './context/TileStandardContext';
import TileSizeContext from './context/TileSizeContext';
import TileStandardHeader from './header/TileStandardHeader';
import TileStandardTabs from './tabs/TileStandardTabs';
import TileStandardContent from './content/TileStandardContent';
import TileStandardFooter from './footer/TileStandardFooter';

import {
  getActiveChannel,
  getImagesFromStandardImages,
  getLoadingImages,
  getNumPostsToShow,
  getTabs
} from './utils/tile-standard';
import { addPlaceholderImages, buildImage } from 'utils/image';

import './tile-standard.scss';

import type {
  ImageGridImage,
  ListContextMenu,
  TileStandard as TileStandardAlias,
  TileStandardContext as TileStandardContextAlias,
  TileStandardSize,
  TileStandardType
} from 'types';

type Props = {
  creator: TileStandardAlias,
  channel: string | undefined | null,
  recentPosts?: Array<ImageGridImage>,
  hasMorePostsToLoad: boolean,
  globalTileSize?: TileStandardSize,
  tileType: TileStandardType,
  tileIndex?: number,
  contextMenuProps?: ListContextMenu,
  isFreemium?: boolean,
  updateCreatorTileSize?: (size: string, creatorId: number) => void,
  onDelete?: (creator: TileStandardAlias) => void,
  onFetchInfluencerPosts?: (c: number, b: string, a: boolean) => void
};

const EXPANDED = 'expanded';
const COLLAPSED = 'collapsed';

const TileStandard = ({
  creator,
  channel,
  recentPosts = [],
  hasMorePostsToLoad,
  globalTileSize,
  tileType,
  tileIndex,
  contextMenuProps,
  isFreemium,
  updateCreatorTileSize,
  onDelete,
  onFetchInfluencerPosts = () => {}
}: Props): JSX.Element => {
  const channels = creator.channels;
  const tabs = getTabs(channels);

  const [tileSize, setTileSize] = React.useState(globalTileSize || EXPANDED);
  const [activeTab, setActiveTab] = React.useState(channel || tabs[0].identifier);
  const [carouselIndex, setCarouselIndex] = React.useState(0);

  const activeChannel = getActiveChannel(channels, activeTab);
  const numItemsToShow = getNumPostsToShow(activeTab);

  const lastCarouselPage = Math.ceil(recentPosts.length / numItemsToShow) - 1;
  const isLastPageFullyFilled = recentPosts.length % numItemsToShow === 0;
  const hasMorePostsToScroll = (carouselIndex < lastCarouselPage)
    || (carouselIndex === lastCarouselPage && hasMorePostsToLoad && isLastPageFullyFilled);
  const standardImages = recentPosts.map(post => buildImage(post));
  const carouselItems = getImagesFromStandardImages(standardImages, numItemsToShow, carouselIndex);
  const items = ((carouselItems.length === 0 && hasMorePostsToLoad) && hasMorePostsToScroll)
    ? getLoadingImages(numItemsToShow)
    : addPlaceholderImages(carouselItems, numItemsToShow, hasMorePostsToLoad);

  const handleSetActiveTab = (updatedActiveTab: string): void => {
    setActiveTab(updatedActiveTab);
    setCarouselIndex(0);
    if (updatedActiveTab !== 'residual') {
      onFetchInfluencerPosts(creator.id, updatedActiveTab, true);
    }
  };

  const handleSetCarouselIndex = (index: number): void => {
    setCarouselIndex(index);
    if (index >= lastCarouselPage - 1) {
      onFetchInfluencerPosts(creator.id, activeTab, false);
    }
  };

  const overlaySize = isFreemium && globalTileSize === 'collapsed'
    ? 'small'
    : 'normal';

  // when freemium, first result is static (COLLAB-12525)
  const blockComponents = isFreemium && isNumber(tileIndex) && tileIndex > 0;

  const containerClasses = classnames('tile-standard', {
    'tile-standard--prevent-focus': blockComponents
  });

  const context = React.useMemo<TileStandardContextAlias>(() => ({
    activeTab,
    creator,
    channels,
    recentPosts: [...recentPosts],
    carouselItems: items,
    carouselIndex,
    galleryModalIndexStart: carouselIndex * numItemsToShow,
    hasNextRecentPostsPage: hasMorePostsToScroll,
    isLoading: hasMorePostsToLoad && !hasMorePostsToScroll,
    isFreemium,
    tileIndex,
    tabs,
    onSetCarouselIndex: handleSetCarouselIndex,
    onGalleryLastPage: () => onFetchInfluencerPosts(creator.id, activeTab, false)
  }), [activeTab, creator, recentPosts, channels, carouselIndex, hasMorePostsToLoad]);

  const updateTileSize = (type: string) => {
    setTileSize(type);

    if (updateCreatorTileSize) {
      updateCreatorTileSize(type, creator.id);
    }
  };

  React.useEffect(() => {
    setTileSize(globalTileSize || EXPANDED);
  }, [globalTileSize]);

  return (
    <TileSizeContext.Provider value={tileSize}>
      <TileStandardContext.Provider value={context}>
        <div
          id={`tile-standard__overlay-wrapper--${creator?.id}`}
          className={containerClasses}
          tabIndex={0} // allows `Overlay` to trigger on focus
        >
          <TileStandardHeader
            onResize={updateTileSize}
          />
          <TileStandardTabs
            isDisabled={tileSize === COLLAPSED}
            onClick={handleSetActiveTab}
          />
          <TileStandardContent
            activeChannel={activeChannel}
          />
          <TileStandardFooter
            creator={creator}
            tileType={tileType}
            contextMenuProps={contextMenuProps}
            isHidden={tileSize === COLLAPSED}
            onDelete={onDelete}
          />
          {
            blockComponents && (
              <React.Fragment>
                <Overlay
                  parentId={`tile-standard__overlay-wrapper--${creator?.id}`}
                  showOnHover
                  showOnFocus
                  withNoBackgroundColor
                >
                  <TileStandardOverlay
                    size={overlaySize}
                  />
                </Overlay>
                <div className="tile-standard__overlay-mask" />
              </React.Fragment>
            )
          }
        </div>
      </TileStandardContext.Provider>
    </TileSizeContext.Provider>
  );
};

export default TileStandard;
